import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

// Helpers
import { useTranslate } from "helpers";
import { getActiveUser } from "services/api/users";

// UI
import { Button } from "ui/components/Button/Button";
import { Modal, ModalIntro } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import ModalHeader from "ui/components/Modal/ModalHeader";
import invoiceAnimation from "ui/theme/animations/invoice.json";
import VehicleServiceEditCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleServiceEditCard/VehicleServiceEditCard";
import VehicleStatusDetailForm from "../VehicleStatusDetailForm/VehicleStatusDetailForm";
import VehicleStatusPicker from "../VehicleStatusPicker/VehicleStatusPicker";

// Servisni sloj
import { updateFleetVehicleStatusAPI } from "services/api/fleet/vehicles/api";
import {
  initialFleetVehicleStatusDetailForm,
  statusRequiresDetails,
} from "services/api/fleet/vehicles/consts";
import {
  FleetVehicleStatusDetailFormValues,
  FleetVehicleStatusEnum,
} from "services/api/fleet/vehicles/types";
import { initialVehicleLocationForm } from "services/api/fleet/vehicles/locations/consts";
import {
  VehicleLocationFormValues,
  VehicleLocationTypeEnum,
} from "services/api/fleet/vehicles/locations/types";
import { FleetVehicleServiceEditFormValues } from "services/api/fleet/vehicles/services/types";
import { buildServiceStatusDetail } from "services/api/fleet/vehicles/services/transformations";

// Validacija
import { Errors } from "services/forms/validation/types";
import { clearChangedErrors, validateYup } from "services/forms/validation/validation";
import { buildStatusDetailSchema } from "../VehicleStatusDetailForm/validation";
import { getStatusField } from "../VehicleStatusDetailForm/fields";

import { VehicleStatusFlowProps } from "./types";

/**
 * Promjena statusa vozila — od odabira do spremanja, u jednom modalu.
 *
 * Prvi korak je odabir statusa, drugi kontekst koji taj status traži. Status bez dodatnih
 * podataka nema drugi korak, pa se sprema odmah — bez posebnog dijaloga potvrde.
 *
 * Odvojena je od značke jer se status mijenja i s drugih mjesta (nakon unosa štete), a
 * tabovi vozila su zasebne rute, pa zajedničkog roditelja koji bi to držao nema.
 */
const VehicleStatusFlow: React.FC<VehicleStatusFlowProps> = (props) => {
  const {
    show,
    vehicleId,
    currentStatus,
    status: lockedStatus,
    prefill,
    skipServiceOrder,
    skipLocation,
    autoSave,
    onClose,
    onSaved,
  } = props;

  const { t } = useTranslate();
  const { user } = useSelector(getActiveUser);
  const currentUserOfficeId = user?.officeId ?? null;

  // Izbornik se otvara na zatečenom statusu — vidi se s čega se mijenja, a gumb ostaje
  // zaključan dok se ne odabere neki drugi.
  const [selectedStatus, setSelectedStatus] = useState<FleetVehicleStatusEnum | null>(
    lockedStatus ?? currentStatus
  );
  const [isDetailStep, setIsDetailStep] = useState<boolean>(!!lockedStatus);
  const [formValues, setFormValues] = useState<FleetVehicleStatusDetailFormValues>(
    initialFleetVehicleStatusDetailForm
  );
  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showServiceWizard, setShowServiceWizard] = useState<boolean>(false);

  // Kad je vozilo već premješteno, lokacija se ne pita — ostaje ona koju je premještaj upisao.
  const locationField =
    selectedStatus && !skipLocation ? getStatusField(selectedStatus, "location") : undefined;

  /**
   * Status koji otvara servisni nalog umjesto obrasca — detalj mu je dokument, ne polja.
   * Iznimka je tok pokrenut s naloga koji već postoji: tada se nalog ne otvara ponovno.
   */
  const isServiceStatus = selectedStatus === FleetVehicleStatusEnum.UNAVAILABLE_SERVICE;
  const needsServiceOrder = isServiceStatus && !skipServiceOrder;

  const hasDetails =
    !!selectedStatus && statusRequiresDetails(selectedStatus, !!skipLocation);

  // Otvaranje kreće od zadanog statusa: iz značke od praznog izbora, iz štete od
  // već poznatog statusa i njegovih polja. Zadani servis odmah otvara nalog — vozilo
  // ne smije biti "na servisu" bez njega.
  useEffect(() => {
    if (!show) return;

    const locked = lockedStatus ?? null;
    const opensServiceOrder =
      !!locked && locked === FleetVehicleStatusEnum.UNAVAILABLE_SERVICE && !skipServiceOrder;

    setSelectedStatus(locked ?? currentStatus);
    setIsDetailStep(!!locked && !opensServiceOrder);
    setShowServiceWizard(opensServiceOrder);
    setErrors({});
  }, [show, lockedStatus, currentStatus, skipServiceOrder]);

  // Polja kreću od nule za svaki odabrani status — druga polja traži drugi status.
  useEffect(() => {
    if (!show || !selectedStatus) return;

    setErrors({});

    // Lokacija kreće od vrste koju status podrazumijeva; nositelja popunjava efekt ispod.
    setFormValues({
      ...initialFleetVehicleStatusDetailForm,
      location: locationField
        ? {
            ...initialVehicleLocationForm,
            locationType:
              (locationField.locationType as VehicleLocationTypeEnum) ??
              initialVehicleLocationForm.locationType,
            officeId:
              locationField.locationDefault === "userOffice"
                ? currentUserOfficeId ?? null
                : null,
          }
        : null,
      // Ono što pozivatelj već zna ide preko zadanih vrijednosti.
      ...prefill,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, selectedStatus, locationField, currentUserOfficeId]);

  // Nositelj lokacije iz odabranog kontakta (Servis -> taj servis, U dolasku -> dobavljač).
  // Vrijedi dok je lokacija one vrste koju status podrazumijeva — obrazac ondje i ne
  // prikazuje nositelja. Čim korisnik odabere drugu vrstu, lokaciju vodi sam.
  useEffect(() => {
    if (!show) return;
    if (locationField?.locationDefault !== "contact") return;
    if (!formValues.contactId) return;
    if (formValues.location?.locationType !== locationField.locationType) return;

    setFormValues((prev) =>
      prev.location && prev.location.contactId !== formValues.contactId
        ? { ...prev, location: { ...prev.location, contactId: formValues.contactId } }
        : prev
    );
  }, [show, locationField, formValues.contactId, formValues.location?.locationType]);

  const handleStatusSelect = useCallback((status: FleetVehicleStatusEnum) => {
    setSelectedStatus(status);
  }, []);

  const handleFieldsChange = useCallback(
    (patch: Partial<FleetVehicleStatusDetailFormValues>) => {
      clearChangedErrors(formValues, patch, setErrors);
      setFormValues((prev) => ({ ...prev, ...patch }));
    },
    [formValues]
  );

  // Shema lokaciju ugnijezdi, pa su i putanje grešaka ugniježđene.
  const handleLocationChange = useCallback(
    (location: VehicleLocationFormValues) => {
      clearChangedErrors(formValues.location ?? {}, location, setErrors, "location.");
      setFormValues((prev) => ({ ...prev, location }));
    },
    [formValues.location]
  );

  const saveStatus = useCallback(
    async (details?: FleetVehicleStatusDetailFormValues) => {
      if (!vehicleId || !selectedStatus) return;

      setIsSaving(true);
      try {
        await updateFleetVehicleStatusAPI(vehicleId, selectedStatus, t, details);
        onSaved(selectedStatus);
        onClose();
      } catch (err) {
        // Greška se prikazuje kroz API interceptor; modal ostaje otvoren da se unos ne izgubi.
        console.error("Greška prilikom promjene statusa:", err);
      } finally {
        setIsSaving(false);
      }
    },
    [vehicleId, selectedStatus, t, onSaved, onClose]
  );

  /**
   * Zadani status koji nema što pitati sprema se odmah — pozivatelj je potvrdu već tražio
   * (npr. povratak u poslovnicu nakon premještaja, gdje je jedino polje lokacija).
   */
  const autoSavedRef = useRef<boolean>(false);

  /**
   * Detalj složen od onoga što pozivatelj već zna. Ide iz `prefill`-a, ne iz `formValues`:
   * stanje se puni efektom iznad, pa je u istom prolazu još prazno.
   */
  const prefilledDetail = useMemo(
    (): FleetVehicleStatusDetailFormValues => ({
      ...initialFleetVehicleStatusDetailForm,
      ...prefill,
    }),
    [prefill]
  );

  /**
   * Nema se što pitati: status ili nema dodatnih polja, ili je pozivatelj sve donio sa
   * sobom. Nepotpun `prefill` (npr. servisna lokacija bez kontakta) vraća tok na pitanje.
   */
  const isAutoSaving =
    !!lockedStatus &&
    !needsServiceOrder &&
    (!hasDetails ||
      (!!autoSave &&
        !!selectedStatus &&
        buildStatusDetailSchema(selectedStatus).isValidSync(prefilledDetail)));

  useEffect(() => {
    if (!show) {
      autoSavedRef.current = false;
      return;
    }

    if (!isAutoSaving || autoSavedRef.current) return;

    autoSavedRef.current = true;
    saveStatus(hasDetails ? prefilledDetail : undefined);
  }, [show, isAutoSaving, hasDetails, prefilledDetail, saveStatus]);

  /**
   * Dalje ili Spremi, ovisno o koraku:
   * - status bez dodatnih polja se sprema odmah;
   * - servis otvara nalog;
   * - ostali statusi vode na drugi korak, a s njega na spremanje.
   */
  const handleSubmit = useCallback(async () => {
    if (!selectedStatus) return;

    if (!isDetailStep) {
      if (needsServiceOrder) {
        setShowServiceWizard(true);
        return;
      }

      if (hasDetails) {
        setIsDetailStep(true);
        return;
      }

      await saveStatus();
      return;
    }

    const isValid = validateYup(
      formValues,
      buildStatusDetailSchema(selectedStatus),
      setErrors
    );
    if (!isValid) return;

    await saveStatus(formValues);
  }, [
    selectedStatus,
    isDetailStep,
    needsServiceOrder,
    hasDetails,
    formValues,
    saveStatus,
  ]);

  const handleBack = useCallback(() => {
    setIsDetailStep(false);
  }, []);

  /**
   * Nalog i status idu jednim zahtjevom — backend ih upisuje u istoj transakciji, pa
   * vozilo ne može ostati "na servisu" bez naloga.
   */
  const handleServiceSubmit = useCallback(
    async (
      service: FleetVehicleServiceEditFormValues,
      location: VehicleLocationFormValues | null
    ) => {
      await saveStatus(buildServiceStatusDetail(service, location));
      setShowServiceWizard(false);
    },
    [saveStatus]
  );

  /**
   * Odustajanje na nalogu vraća na odabir statusa. Kad je status zadan izvana, odabira
   * nema — nalog je jedini korak, pa se zatvara cijeli tok.
   */
  const handleServiceClose = useCallback(() => {
    setShowServiceWizard(false);
    if (lockedStatus) onClose();
  }, [lockedStatus, onClose]);

  // Čarobnjak naloga ide preko cijelog ekrana i dijeli razinu s ovim modalom, pa se
  // promjena statusa sklanja dok je nalog otvoren. Kad se status sprema bez pitanja,
  // modal se uopće ne crta — inače bi bljesnuo i odmah se zatvorio.
  const showStatus = show && !showServiceWizard && !isAutoSaving;

  const canGoBack = isDetailStep && !lockedStatus;

  return (
    <>
      <Modal
        show={showStatus}
        onHide={onClose}
        size="full"
        position="side"
        intro={{
          content: (
            <ModalIntro
              animation={invoiceAnimation}
              title={isDetailStep ? "Još samo nekoliko podataka!" : "Gdje je vozilo sada?"}
              description={
                isDetailStep
                  ? "Status sam ne kaže dovoljno — zabilježite gdje je vozilo, do kada i zbog čega, da se poslije zna što se dogodilo."
                  : "Status govori je li vozilo dostupno i zašto nije. Svaka promjena ostaje zapisana u povijesti vozila."
              }
              show={showStatus}
            />
          ),
        }}
      >
        <ModalHeader
          icon="/media/icons/status.svg"
          title="admin.fleet.vehicle.status.modal.title"
          onHide={onClose}
        />
        {/* Podnožje je pravi ModalFooter, pa donji razmak za "fixed" podnožje otpada. */}
        <ModalBody fixedFooterSpacing={false}>
          {isDetailStep && selectedStatus ? (
            <VehicleStatusDetailForm
              vehicleId={vehicleId}
              status={selectedStatus}
              values={formValues}
              errors={errors}
              onChange={handleFieldsChange}
              onLocationChange={handleLocationChange}
            />
          ) : (
            <div className="flex items-start justify-center px-4 pt-12 pb-6">
              <div className="w-full max-w-4xl">
                <h1 className="text-2xl font-bold text-slate-900 text-center mb-2">
                  Status vozila
                </h1>
                <p className="text-center text-md text-slate-500 mb-8">
                  Odaberite u kojem je stanju vozilo od sada.
                </p>

                <div className="border bg-white border-slate-200">
                  <VehicleStatusPicker
                    value={selectedStatus}
                    onChange={handleStatusSelect}
                  />
                </div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <div className="px-4 py-3 flex justify-between gap-3">
            <Button variant="outlined" onClick={canGoBack ? handleBack : onClose}>
              {canGoBack ? t("admin.wizard.footer.back") : t("admin.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                isSaving || !selectedStatus || (!isDetailStep && selectedStatus === currentStatus)
              }
            >
              {/* Status bez dodatnih podataka nema drugi korak — gumb je odmah Spremi. */}
              {!isDetailStep && (hasDetails || isServiceStatus)
                ? t("admin.wizard.footer.foward")
                : t("admin.save")}
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* Odlazak na servis otvara nalog; lokacija se predlaže na odabrani servis, a ono
          što je pozivatelj već prikupio (serviser, datum, napomena) ulazi u nalog. */}
      {showServiceWizard && (
        <VehicleServiceEditCard
          show={showServiceWizard}
          vehicleId={vehicleId}
          initialLocation={
            skipLocation
              ? null
              : formValues.location ?? {
                  ...initialVehicleLocationForm,
                  locationType: VehicleLocationTypeEnum.SERVICE,
                  officeId: null,
                }
          }
          initialValues={{
            contactId: formValues.contactId,
            receivedDate: formValues.validFrom ?? new Date(),
            description: formValues.note,
          }}
          isSaving={isSaving}
          onSubmit={handleServiceSubmit}
          onClose={handleServiceClose}
        />
      )}
    </>
  );
};

export default memo(VehicleStatusFlow);
